import { Close, Delete, FormatQuote } from '@mui/icons-material';
import {
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Modal,
  Autocomplete,
  TextField,
} from '@mui/material';
import { Col, Row, message } from "antd";
import { DeleteModal } from "components/Dialog";
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import COLOR from "utils/constants/color";
import BadgeButton from "components/AdminBooks/BadgeButton";

const Wrapper = styled.div`
  height: 100%;
  min-height: 600px;
  width: 100%;
  position: relative;
`;

const Toolbar = styled.div`
  align-items: center;
  border-bottom: 1px solid ${COLOR.black600};
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  padding-bottom: 4px;
  width: 100%;
  min-height: 30px;
  margin-top: 30px;
`;

const ModalContent = styled.div`
  left: 50%;
  max-width: 100vw;
  min-width: 450px;
  padding: 0px 24px;
  position: absolute;
  border-radius: 10px;
  background-color: #fff;
  padding: 10px;
  transform: translate(-50%, -50%);
  top: 50%;
  @media (max-width: 1366px) {
    width: 90vw;
  }

  @media (max-width: 500px) {
    max-height: 560px;
  }
`;

const ModalConfigArea = styled.div`
  max-height: 75vh;
  overflow: auto;
  margin-top: 10px;
`;

const LinkArea = styled.div`
  padding: 10px;
  margin: 2px;
  border: 1px dashed;
  margin-top: 24px;
`;

const TitleText = styled.span`
  font-size: 18px;
  color: black;
  font-weight: 600;
`;

const ToolBarButton = ({
  planData,
  pageId,
  bookId,
  updatePlan,
  onRemoveAssignmentPlan,
  totalPages,
  pages: currentBookPages = [],
  hasPermission = true
}) => {
  const showAlert = useAlert();
  const [plan, setPlan] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const onClose = () => {
    setOpenModal(false);
  };

  const disableBtn = () => {
    return (
      !Boolean(plan) ||
      !(plan.from && plan.from >= 0 && plan.to && plan.to >= 0)
    );
  };

  const onUpdate = () => {
    try {
      const from = parseInt(plan.from);
      const to = parseInt(plan.to);

      if (!from || !to) {
        showAlert("All fields are required.", "error");
        return;
      }

      if (from >= to) {
        showAlert("'From' page must be less than 'To' page.", "error");
        return;
      }

      if (to - from > 9) {
        showAlert("AI can't generate more than 10 images.", "error");
        return;
      }

      updatePlan(plan);
      setOpenModal(false);
    } catch (error) {
      console.log(error);
    }
  };

  const dropdownPages = useMemo(() => {
    if (currentBookPages && Array.isArray(currentBookPages) && currentBookPages.length > 0) {
      return currentBookPages.map((page) => ({
        ...page,
        NumberMasked: page.NumberMasked || `Page ${page.pageId}`,
        pageId: parseInt(page.pageId)
      }));
    }
    return [];
  }, [currentBookPages]);

  useEffect(() => {
    if (planData) {
      setPlan(planData);
    } else {
      setPlan({
        page: pageId,
        from: "",
        to: "",
      });
    }
  }, [planData,pageId]);


  return (
    <>
      <DeleteModal
        deleteToggle={deleteModal}
        onDelete={() => {
          onRemoveAssignmentPlan(pageId);
          setDeleteModal(false);
          setOpenModal(false);
        }}
        setDeleteToggle={setDeleteModal}
        message={`Are you sure you want to remove the sentence starter plan on pageId ${pageId}?`}
      />
      <Modal open={openModal} onClose={onClose}>
        <ModalContent>
          <Row style={{ justifyContent: "space-between" }}>
            <TitleText>Sentence Starter</TitleText>
            <IconButton onClick={onClose} style={{ padding: 0 }}>
              <Close color="secondary" />
            </IconButton>
          </Row>
          <ModalConfigArea>
            <LinkArea>
              <Row
                gutter={[24, 24]}
                style={{ justifyContent: "flex-end", display: "flex" }}
              >
                {planData ? (
                  <IconButton
                    onClick={() => setDeleteModal(true)}
                    style={{ padding: 0, marginRight: 10 }}
                  >
                    <Delete color="secondary" />
                  </IconButton>
                ) : null}
              </Row>
              <Row gutter={[24, 24]} style={{ marginTop: 10 }}>
                <Col span={24}>
                  <TextField
                    id="pageId"
                    name="pageId"
                    label="PageId"
                    disabled
                    InputLabelProps={{ shrink: true, required: true }}
                    type="number"
                    variant="outlined"
                    value={pageId}
                  />
                </Col>
              </Row>
              <Row gutter={[24, 24]} style={{ marginTop: 20 }}>
                <Col span={24}>
                  <FormControl className='floatlabelCstm' variant="outlined" fullWidth>
                    <InputLabel id="from-page-label">From PageId</InputLabel>
                    <Autocomplete
                      id="fromPage"
                      options={dropdownPages || []}
                      getOptionLabel={(option) => {
                        if (!option || typeof option !== 'object') return '';
                        const pageId = option.pageId != null ? option.pageId : '';
                        return String(option.NumberMasked || `Page ${pageId}` || '');
                      }}
                      value={dropdownPages?.find(page => page.pageId == parseInt(plan?.from)) || null}
                      onChange={(event, newValue) => {
                        const selectedFromPageId = newValue ? newValue.pageId : '';
                        setPlan({
                          ...plan,
                          from: selectedFromPageId,
                          to: '',
                        });
                      }}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="From PageId" 
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              padding: '0px'
                            },
                            '& .MuiOutlinedInput-input': {
                              padding: '14px 14px'
                            }
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Col>
              </Row>
              <Row gutter={[24, 24]} style={{ marginTop: 20 }}>
                <Col span={24}>
                  <FormControl className='floatlabelCstm' variant="outlined" fullWidth>
                    <InputLabel id="to-page-label">To PageId</InputLabel>
                    <Autocomplete
                      id="toPage"
                      options={(() => {
                        if (!plan?.from || !dropdownPages || !currentBookPages?.length) return [];
                        
                        // Find the index of the 'from' pageId in the currentBookPages array
                        const fromIndex = currentBookPages.findIndex(page => page.pageId == plan.from);
                        
                        if (fromIndex < 0) return [];
                        
                        // Filter pages that have index >= fromIndex
                        // Since dropdownPages is mapped from currentBookPages in the same order, indices match
                        return dropdownPages.filter((page, index) => {
                          // Find the index of this page in currentBookPages to ensure correctness
                          const pageIndex = currentBookPages.findIndex(p => p.pageId == page.pageId);
                          return pageIndex >= fromIndex && pageIndex >= 0;
                        })
                      })()}
                      getOptionLabel={(option) => {
                        if (!option || typeof option !== 'object') return '';
                        const pageId = option.pageId != null ? option.pageId : '';
                        return String(option.NumberMasked || `Page ${pageId}` || '');
                      }}
                      value={(() => {
                        if (!plan?.to || !dropdownPages) return null;
                        return dropdownPages.find(page => page.pageId == parseInt(plan?.to)) || null;
                      })()}
                      onChange={(event, newValue) => {
                        setPlan({
                          ...plan,
                          to: newValue ? newValue.pageId : '',
                        });
                      }}
                      disabled={!plan?.from}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="To PageId" 
                          variant="outlined"
                          placeholder={!plan?.from ? "Please select 'From Page' first" : "Select ending page"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              padding: '0px'
                            },
                            '& .MuiOutlinedInput-input': {
                              padding: '14px 14px'
                            }
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Col>
              </Row>
              <Row
                gutter={[24, 24]}
                style={{ marginTop: 20, marginRight: 0, marginLeft: 0 }}
              >
                <Button
                  fullWidth
                  disabled={disableBtn()}
                  onClick={() => {
                    onUpdate();
                  }}
                  color="primary"
                  variant="contained"
                >
                  Update
                </Button>
              </Row>
            </LinkArea>
          </ModalConfigArea>
        </ModalContent>
      </Modal>
      <BadgeButton
        onClick={() => {
          if (!hasPermission) {
            message.warning('You do not have permission to access this feature.');
            return;
          }
          setOpenModal(true);
        }}
        startIcon={<FormatQuote />}
        showBadge={planData}
        backgroundColor="#dc2626"
      >
        Sentence Starter 
      </BadgeButton>
    </>
  );
};

export default ToolBarButton;
